import React, { useState, useEffect, useCallback } from 'react';
import { Users, Bell, Mail, TrendingUp, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import api from '../api/axios';
import { useToast } from '../components/Toast';
import ClientsTable  from '../components/clients/ClientsTable';
import ClientDrawer  from '../components/clients/ClientDrawer';
import RappelSuivi   from '../components/clients/RappelSuivi';
import EmailTemplate from '../components/clients/EmailTemplate';

const TABS = [
  { key: 'clients',  label: 'Clients',        icon: Users },
  { key: 'suivi',    label: 'Suivi rappels',  icon: Bell  },
  { key: 'template', label: 'Modèle email',   icon: Mail  },
];

function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl ${bg} border border-transparent`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={16} />
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 leading-none mb-0.5">{label}</p>
        <p className="text-xl font-bold text-slate-800 leading-none">{value}</p>
      </div>
    </div>
  );
}

export default function AdminClients() {
  const showToast = useToast();
  const [tab,      setTab]      = useState('clients');
  const [clients,  setClients]  = useState([]);
  const [services, setServices] = useState([]);
  const [salon,    setSalon]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [drawer,   setDrawer]   = useState(null);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, sRes, salRes] = await Promise.all([
        api.get('/clients'),
        api.get('/clients/services'),
        api.get('/salon/me'),
      ]);
      setClients(cRes.data.data || []);
      setServices(sRes.data.data || []);
      setSalon(salRes.data.data || null);
    } catch { showToast('Erreur chargement', 'error'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { loadClients(); }, [loadClients]);

  const handleSaved = () => { setDrawer(null); loadClients(); };

  const counts = clients.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; }, {});
  const total  = clients.length;

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-auto">

      {/* ── Header ── */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 pt-6 pb-0">
        <div className="flex items-baseline justify-between mb-5">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900">Clients & Rappels</h1>
            <p className="text-sm text-slate-400 mt-0.5 break-words">Base clients, rappels automatiques et suivi des passages</p>
          </div>
        </div>

        {/* Stats */}
        {!loading && total > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
            <StatCard icon={TrendingUp}   label="Total clients" value={total}
              color="bg-indigo-100 text-indigo-600"
              bg="bg-slate-50" />
            <StatCard icon={CheckCircle}  label="À jour"       value={counts.a_jour    || 0}
              color="bg-emerald-100 text-emerald-600"
              bg="bg-emerald-50/60" />
            <StatCard icon={Clock}        label="Bientôt"       value={counts.bientot   || 0}
              color="bg-amber-100 text-amber-600"
              bg="bg-amber-50/60" />
            <StatCard icon={AlertCircle}  label="En retard"    value={counts.en_retard || 0}
              color="bg-red-100 text-red-600"
              bg="bg-red-50/60" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-0 -mb-px overflow-x-auto no-scrollbar">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap shrink-0
                  ${active
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 px-4 sm:px-6 py-6">
        {tab === 'clients' && (
          <ClientsTable
            clients={clients} loading={loading}
            onEdit={c => setDrawer(c)}
            onNew={() => setDrawer('new')}
            onRefresh={loadClients}
            currency={salon?.devise} />
        )}
        {tab === 'suivi'    && <RappelSuivi />}
        {tab === 'template' && <EmailTemplate />}
      </div>

      {drawer !== null && (
        <ClientDrawer
          client={drawer === 'new' ? null : drawer}
          services={services}
          allowedFrequencies={salon?.allowedFrequencies}
          devise={salon?.devise}
          onClose={() => setDrawer(null)}
          onSaved={handleSaved} />
      )}
    </div>
  );
}
